import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { advancedPositionAlternatives } from '../../utils/advancedStatistics';
import { reportAdvancedModel, reportAdvancedPeriod, reportAdvancedScope, reportAdvancedTactics } from '../../utils/rivalAdvancedReport';

export default function ReportAdvanced({ statistics, otherStatistics, match, home, name, otherName,
  players = [], scope = 'match', period = 'partido', onFilter, onSection, summary = false }) {
  const { t, i18n } = useTranslation('reports');
  const id = useId();
  const context = { idTemporada: match.idTemporada, idCompeticion: match.idCompeticion, idGrupo: match.idGrupo };
  const ownScope = reportAdvancedScope(scope, home), otherScope = reportAdvancedScope(scope, home === null ? null : !home);
  const selectedPeriod = reportAdvancedPeriod(period);
  const own = reportAdvancedModel(statistics, { ...context,
    idEquipo: home === true ? match.codEquipoLocal : home === false ? match.codEquipoVisitante : null }, ownScope, selectedPeriod);
  const other = reportAdvancedModel(otherStatistics, { ...context,
    idEquipo: home === true ? match.codEquipoVisitante : home === false ? match.codEquipoLocal : null }, otherScope, selectedPeriod);
  const comparable = own.source && own.source === other.source && statistics?.idEquipo !== otherStatistics?.idEquipo;
  const format = (value, percentage = false) => value === null ? t('advanced.unknown')
    : `${value.toLocaleString(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${percentage ? ' %' : ''}`;
  const sample = row => row.value === null ? t('advanced.noSample') : t('advanced.sample', { count: row.sample });
  const scopeText = `${name} · ${t(`advanced.scopes.${ownScope}`)} · ${t(`advanced.periods.${selectedPeriod}`)}`;
  const tacticalPlayers = players.filter(player => Object.entries({ ...context, idEquipo: statistics?.idEquipo })
    .every(([key, value]) => value != null && String(player[key]) === String(value)));
  const tactics = reportAdvancedTactics(own.tactics, tacticalPlayers);
  const lineup = tactics.players.length ? [1, ...tactics.formation.split('-').map(Number)].map((count, index, counts) => {
    const start = counts.slice(0, index).reduce((a, b) => a + b, 0);
    return tactics.players.slice(start, start + count);
  }) : [];
  const alternatives = advancedPositionAlternatives(own.tactics?.sistemas?.[tactics.formation || tactics.systems[0]?.name]);
  if (summary && !own.available) return null;
  return <section className={`rr-panel rr-advanced ${summary ? 'rr-advanced-summary' : ''}`} aria-labelledby={`${id}-title`}>
    <header><span className="rr-eyebrow">{t('advanced.eyebrow')}</span>
      <h2 id={`${id}-title`}>{t(summary ? 'advanced.snapshot' : 'advanced.title')}</h2>
      <p>{scopeText}</p></header>
    {!summary && <div className="rr-advanced-filters">
      <div className="rr-advanced-filter"><label htmlFor={`${id}-scope`}>{t('advanced.scope')}</label>
      <select id={`${id}-scope`} value={scope} onChange={event => onFilter('advancedScope', event.target.value)}>
        {['match', 'total', 'local', 'visitante'].map(key => <option key={key} value={key}>{t(`advanced.scopes.${key}`)}</option>)}
      </select></div>
      <div className="rr-advanced-filter"><label htmlFor={`${id}-period`}>{t('advanced.period')}</label>
      <select id={`${id}-period`} value={selectedPeriod} onChange={event => onFilter('advancedPeriod', event.target.value)}>
        {['partido', 'primeraParte', 'segundaParte'].map(key => <option key={key} value={key}>{t(`advanced.periods.${key}`)}</option>)}
      </select></div>
      <p>{t('advanced.method')}</p>
    </div>}
    {!own.available ? <p className="rr-empty">{t('advanced.empty')}</p> : <>
      <dl className="rr-kpis rr-advanced-kpis">{own.rows.slice(0, 6).map(row => <div key={row.key}>
        <dt>{t(`advanced.metrics.${row.key}`)}</dt><dd>{format(row.value, row.percentage)}</dd>
        <small>{row.percentage ? t('advanced.percent') : t('advanced.perMatch')} · {sample(row)}</small>
      </div>)}</dl>
      {summary ? <button className="rr-text-action" type="button" onClick={() => onSection('advanced')}>{t('advanced.open')}</button> : <>
        <p className="rr-note" role="status">{t('advanced.comparison', { team: name, own: t(`advanced.scopes.${ownScope}`),
          other: otherName, away: t(`advanced.scopes.${otherScope}`) })}
          {(!comparable || !other.available) && ` ${t('advanced.noComparison')}`}</p>
        <div className="rr-advanced-columns">{['attack', 'defense', 'control'].map(group => <section key={group} aria-labelledby={`${id}-${group}`}>
          <h3 id={`${id}-${group}`}>{t(`advanced.groups.${group}`)}</h3>
          <div className="rr-data-table"><table aria-label={t(`advanced.groups.${group}`)}>
            <thead><tr><th scope="col">{t('advanced.indicator')}</th><th scope="col">{name}</th>
              <th scope="col">{otherName}</th></tr></thead>
            <tbody>{own.rows.filter(row => row.group === group).map(row => {
              const comparison = comparable ? other.rows.find(item => item.key === row.key) : null;
              const max = Math.max(row.value ?? 0, comparison?.value ?? 0, 1);
              return <tr key={row.key}><th scope="row">{t(`advanced.metrics.${row.key}`)}
                <small>{row.percentage ? '%' : t('advanced.perMatch')}</small></th>
                {[row, comparison].map((item, index) => <td key={index}>
                  <strong>{format(item?.value ?? null, row.percentage)}</strong>
                  <small>{item ? sample(item) : t('advanced.noSample')}</small>
                  <span className={`rr-advanced-bar rr-advanced-bar-${index}`} aria-hidden="true">
                    <i style={{ width: `${(item?.value ?? 0) / max * 100}%` }} /></span>
                </td>)}</tr>;
            })}</tbody>
          </table></div>
        </section>)}</div>
        <section className="rr-advanced-tactics" aria-labelledby={`${id}-tactics`}>
          <header><h3 id={`${id}-tactics`}>{t('advanced.tactics')}</h3>
            <p>{t('advanced.tacticsMethod')}</p></header>
          <div className="rr-advanced-tactics-grid">
            <div><h4>{t('advanced.systems')}</h4>
              <p>{tactics.sample === null ? t('advanced.noSample') : t('advanced.sample', { count: tactics.sample })}</p>
              {tactics.systems.length ? <ul className="rr-advanced-systems">{tactics.systems.map(system => <li key={system.name}>
                <strong>{system.name}</strong><span>{t('advanced.sample', { count: system.count })} · {format(system.share, true)}</span>
                <span className="rr-advanced-bar" aria-hidden="true"><i style={{ width: `${system.share}%` }} /></span>
              </li>)}</ul> : <p>{t('advanced.noTactics')}</p>}
            </div>
            <div><h4>{t('advanced.lineup')}</h4>
              {lineup.length ? <>
                <p>{tactics.formation} · {t('advanced.repetitions', { count: tactics.repetitions })}</p>
                <div className="rr-advanced-pitch" role="group" aria-label={t('advanced.lineup')}>
                  <svg viewBox="0 0 100 120" preserveAspectRatio="none" aria-hidden="true" focusable="false">
                    <rect x="2" y="2" width="96" height="116" /><path d="M2 60h96M22 2v18h56V2M22 118v-18h56v18" /><circle cx="50" cy="60" r="13" />
                  </svg>
                  {lineup.map((row, index) => <div className="rr-advanced-pitch-row" key={index}>{row.map(player =>
                    <span key={player.id} title={player.name || t('advanced.unidentified')}>{player.name || t('advanced.unidentified')}</span>)}</div>)}
                </div>
              </> : <p>{t('advanced.noLineup')}</p>}
            </div>
          </div>
          {alternatives.length > 0 && <details className="rr-advanced-alternatives">
            <summary>{t('advanced.alternatives')}</summary><p>{t('advanced.alternativesMethod')}</p>
            <div className="rr-data-table"><table aria-label={t('advanced.alternatives')}>
              <thead><tr><th scope="col">{t('advanced.slot')}</th><th scope="col">{t('advanced.player')}</th>
                <th scope="col">{t('advanced.appearances')}</th></tr></thead>
              <tbody>{alternatives.flatMap(({ slot, players: candidates }) => candidates.slice(0, 2).map(candidate =>
                <tr key={`${slot}-${candidate.id}`}><th scope="row">{slot}</th><td>{tacticalPlayers.find(player =>
                  String(player.idJugador ?? player.codJugador) === candidate.id)?.nombre || t('advanced.unidentified')}</td>
                <td>{candidate.count} · {format(candidate.percentage, true)}</td></tr>))}</tbody>
            </table></div>
          </details>}
        </section>
      </>}
    </>}
    {own.partial > 0 && <p className="rr-note">{t('advanced.partial', { count: own.partial })}</p>}
  </section>;
}
